import { Box, Stack } from "@mui/material";
import { BulkButtonComponents } from "@components/common/faro-table/faro-table-types";
import { LoadingWrapper } from "@context-providers/loading-state-context";
import { IconButton, Menu, ThreeDotsIcon, neutral } from "@flat-ui";
import { useRef, useState } from "react";
import { useBulkActionsMode } from "@hooks/use-bulk-actions-mode";

export interface FaroBulkActionsButtonsProps {
  /** Buttons shown in table action bar */
  bulkActionsButtons: BulkButtonComponents;

  /** Whether data is loading - automatically handles loading state for all action buttons */
  isLoading: boolean;
}

/**
 * Renders bulk action buttons in a horizontal layout
 * Future enhancement: Show icon or text based on available space
 */
export function FaroTableBulkActionsButtons({
  bulkActionsButtons,
  isLoading,
}: FaroBulkActionsButtonsProps): JSX.Element {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const buttons = Object.values(bulkActionsButtons).filter(Boolean);

  const mode = useBulkActionsMode();

  if (mode === "collapsed") {
    return (
      <>
        <IconButton
          ref={buttonRef}
          onClick={() => setIsMenuOpen(true)}
          size="m"
          margin={0.5}
          color={neutral[0]}
          hoverColor={neutral[0]}
        >
          <ThreeDotsIcon sx={{ transform: "rotate(90deg)" }} />
        </IconButton>

        {/* Keep Menu always mounted to preserve dialog state */}
        <Menu
          open={isMenuOpen}
          anchorEl={buttonRef.current}
          onClose={() => setIsMenuOpen(false)}
          minWidth="7rem"
          keepMounted
        >
          {buttons.map((button, index) => (
            <Box key={index}>
              <LoadingWrapper isLoading={isLoading}>{button}</LoadingWrapper>
            </Box>
          ))}
        </Menu>
      </>
    );
  }

  return (
    <Stack
      sx={{
        flexDirection: "row",
        gap: 1.5,
        alignItems: "center",
      }}
    >
      {buttons.map((button, index) => (
        <Box key={index}>
          <LoadingWrapper isLoading={isLoading}>{button}</LoadingWrapper>
        </Box>
      ))}
    </Stack>
  );
}
